import type { ErrorSummary } from '@pa/api/clinic'
import { DateRangeFilter, FilterMenu, type DateRangeValue, type LocalDate } from '@pa/ui'
import { Building2, Layers, Route } from 'lucide-react'
import { ERROR_PRESETS, errorRangeValue, hasErrorFilters, type ErrorsSearch } from '../model/search'
import { ERROR_STEPS, STEP_LABELS } from '../model/steps'

export interface ErrorFiltersProps {
  search: ErrorsSearch
  summary: ErrorSummary | undefined
  today: LocalDate
  /** S-08: the Clinic and Step filters, with the clinics (#38) to choose from. */
  clinics?: readonly { id: string; name: string }[] | undefined
  onChange: (patch: Partial<ErrorsSearch>) => void
  onClear: () => void
}

/**
 * C-03 filters (UserFlows C-03): Date (presets or custom), Error type ("CODE (n)" from the summary's 90-day counts,
 * API.md §6.8) and Clear filters. S-08 adds Clinic and Step (UserFlows S-08).
 */
export function ErrorFilters({ search, summary, today, clinics, onChange, onClear }: ErrorFiltersProps) {
  return (
    <div className="toolbar filters">
      <DateRangeFilter
        label="Date"
        value={errorRangeValue(search)}
        presets={ERROR_PRESETS}
        today={today}
        onChange={(r: DateRangeValue) => {
          onChange(
            r.id === 'any'
              ? { range: undefined, from: undefined, to: undefined }
              : { range: r.id, from: r.from, to: r.to },
          )
        }}
      />
      {clinics && (
        <FilterMenu
          label="Clinic"
          icon={<Building2 aria-hidden />}
          value={search.clinic ?? null}
          onChange={(v) => {
            onChange({ clinic: v ?? undefined })
          }}
          allLabel="All clinics"
          options={clinics.map((c) => ({ value: c.id, label: c.name }))}
        />
      )}
      {clinics && (
        <FilterMenu
          label="Step"
          icon={<Route aria-hidden />}
          value={search.step ?? null}
          onChange={(v) => {
            onChange({ step: (v ?? undefined) as ErrorsSearch['step'] })
          }}
          allLabel="All steps"
          options={ERROR_STEPS.map((s) => ({ value: s, label: STEP_LABELS[s] }))}
        />
      )}
      <FilterMenu
        label="Error type"
        icon={<Layers aria-hidden />}
        value={search.code ?? null}
        onChange={(v) => {
          onChange({ code: v ?? undefined })
        }}
        allLabel="All types"
        options={(summary?.codes ?? []).map((c) => ({ value: c.code, label: `${c.code} (${String(c.count)})` }))}
      />
      {hasErrorFilters(search) && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClear}>
          Clear filters
        </button>
      )}
    </div>
  )
}
